import { getRouteApi, useParams, useSearch } from "@tanstack/react-router"

import { Button } from "@workspace/web-ui/components/button"
import { Image } from "@workspace/web-ui/components/image";
import { ArrowLeft } from "lucide-react"
import { PatientLogTabs } from "../components/tab/tabs"

import { useQuery } from "@tanstack/react-query"
import { PatientInfo } from "../components/details/program-details"
import { patientDetailsQueryOptions } from "../api/queries"
import { UrgentCarePatientLogTabs } from "../components/tab/urgent-tabs"
import UrgentCareSessionDetails from "../components/urgent-care-session-details"
import { useActiveSessionStore } from "../store/active-session-store"


function PatientLogDetailsSkeleton() {
    return (
        <section
            role="main"
            aria-label="Patient Log Loading"
            className="mx-auto flex flex-1 w-full max-w-[900px] justify-center items-start px-[80px] gap-[52px] max-lg:hidden py-[24px] animate-pulse"
        >
            <div className="flex flex-col gap-5 w-full">
                {/* Back button skeleton */}
                <div className="h-10 w-24 bg-gray-200 rounded-sm" />

                <div className="flex gap-2 items-center w-full">
                    <div className="flex flex-col gap-4 w-full">
                        {/* Patient name skeleton */}
                        <div className="h-8 w-1/3 bg-gray-200 rounded" />
                        {/* Patient info skeleton */}
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="h-4 w-20 bg-gray-200 rounded" />
                                <div className="h-4 w-12 bg-gray-200 rounded" />
                                <div className="h-4 w-10 bg-gray-200 rounded" />
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="h-6 w-24 bg-gray-200 rounded" />
                                <div className="h-6 w-24 bg-gray-200 rounded" />
                            </div>
                        </div>
                        {/* Tabs skeleton */}
                        <div className="flex flex-col gap-4 mt-5 w-full">
                            <div className="flex w-full items-start justify-start overflow-x-auto hide-scrollbar py-1 px-0 bg-transparent border-b gap-1 md:gap-2">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="h-8 w-20 bg-gray-200 rounded-t-md flex-shrink-0 flex-grow-0" />
                                ))}
                            </div>
                            {/* Tab content placeholder - PatientLogTabs handles its own skeleton for content */}
                            <div className="w-full mt-4">
                                <div className="h-64 w-full bg-gray-200 rounded" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function PatientLogDetailsPage() {
    const { id } = useParams({ from: "/(dashboard)/dashboard/$program/patients/patient/$id" })
    const { name, sessionId } = useSearch({ from: "/(dashboard)/dashboard/$program/patients/patient/$id" })
    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();
    const { isOpen } = useActiveSessionStore()

    const { data: patientSummary, isLoading } = useQuery(patientDetailsQueryOptions(id, program))

    const handleBack = () => {
        window.history.back();
    }

    // Show skeleton loading
    if (isLoading) {
        return (<PatientLogDetailsSkeleton />)
    }

    return (
        <section
            aria-label="Quick Actions Overview"
            className="mx-auto  flex flex-1 w-full max-w-[900px] justify-center items-start px-[10px] gap-[52px] py-[24px]">

            {program === "urgent-care" ? (
                (isOpen || sessionId) && patientSummary ? (
                    <div className="flex flex-col gap-5 w-full">

                        <div className="flex gap-2 items-center w-full">

                            <div className="flex flex-col gap-4 w-full">
                                <div className="flex flex-col gap-4 mt-5 w-full">
                                    <UrgentCareSessionDetails patient={patientSummary} fromPage={true} />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-5 w-full">
                        <Button
                            onClick={handleBack}
                            variant="outline"
                            className="w-fit flex items-center gap-2 text-gray-600 hover:text-gray-900 mr-auto px-[16px] py-[10px] rounded-sm"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>

                        <div className="flex gap-2 items-center w-full">

                            <div className="flex flex-col gap-4 w-full">
                                <div className="flex flex-col gap-4 w-full">
                                    <div className="flex gap-2 items-center">
                                    <h1 className="text-[32px] font-semibold">
                                        {typeof name === "string"
                                            ? name
                                                .split(/(?=[A-Z][a-z])|[\s]+/) // split on spaces or camel case
                                                .filter(Boolean)
                                                .join(" ")
                                            : name}
                                    </h1>
                                    {
                                        patientSummary?.urgent_care_onboarded && patientSummary.identity_verified && (
                                            <div className="">
                                                <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                            </div>
                                        )
                                    }
                                    </div>
                                    {patientSummary && (
                                        <PatientInfo patient={patientSummary} program={program ?? ""} />
                                    )}
                                </div>
                                <div className="flex flex-col gap-4 mt-5 w-full">
                                    <UrgentCarePatientLogTabs userId={id} sessionId={sessionId || ""} />
                                </div>
                            </div>
                        </div>
                    </div>
                )
            ) : (
                <div className="flex flex-col gap-5 w-full">
                    <Button
                        onClick={handleBack}
                        variant="outline"
                        className="w-fit flex items-center gap-2 text-gray-600 hover:text-gray-900 mr-auto px-[16px] py-[10px] rounded-sm"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>

                    <div className="flex gap-2 items-center w-full">

                        <div className="flex flex-col gap-4 w-full">
                            <div className="flex flex-col gap-4 w-full">
                                <div className="flex items-center gap-2">
                                <h1 className="text-[32px] font-semibold">
                                    {typeof name === "string"
                                        ? name
                                            .split(/(?=[A-Z][a-z])|[\s]+/) // split on spaces or camel case
                                            .filter(Boolean)
                                            .join(" ")
                                        : name}
                                </h1>
                                {(
                                    patientSummary?.medicalPlan?.payment_status === "paid" && patientSummary.glp1_guide_accepted && patientSummary.identity_verified && patientSummary.supplementary_health_assessment && Object.keys(patientSummary.supplementary_health_assessment).length > 0 && (
                                        <div className="">
                                            <Image src="/icons/check.svg" alt="timeline" layout="constrained" className="w-4 h-4 sm:w-5 sm:h-5" width={96} height={96} />
                                        </div>
                                    )
                                )}
                                </div>
                                {patientSummary && (
                                    <PatientInfo patient={patientSummary} program={program ?? ""} />
                                )}
                            </div>
                            <div className="flex flex-col gap-4 mt-5 w-full">
                                <PatientLogTabs userId={id} program={program} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </section>
    )
}